/**
 * PendingAppointmentsPage
 * Page for viewing and approving/rejecting pending appointments
 * Uses standard table layout matching AppointmentsListPage pattern
 *
 * @version 2025.1.0
 */
import React, { useState } from 'react';
import {
  Typography,
  Button,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  Refresh,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { HelpButton, PageShell, DataTable, type DataTableColumn } from '../../components/common';
import { usePendingAppointments } from '../../hooks/usePendingAppointments';
import appointmentService from '../../services/appointmentService';
import type { PendingAppointmentResponse } from '../../types/appointment';

const columns: DataTableColumn<PendingAppointmentResponse>[] = [
  {
    id: 'date',
    label: 'Data',
    render: (appointment) => appointmentService.formatDate(appointment.selectedDate),
  },
  {
    id: 'time',
    label: 'Horário',
    render: (appointment) =>
      appointmentService.formatTime(appointment.hour, appointment.minute) || '-',
  },
  {
    id: 'employeeName',
    label: 'Funcionário',
    primary: true,
    render: (appointment) => appointment.employeeName,
  },
  { id: 'companyName', label: 'Empresa', render: (appointment) => appointment.companyName },
  { id: 'examType', label: 'Tipo de Exame', render: (appointment) => appointment.examType },
  { id: 'clinicName', label: 'Clínica', render: (appointment) => appointment.clinicName || '-' },
];

const PendingAppointmentsPage: React.FC = () => {
  const {
    appointments,
    loading,
    pagination,
    search,
    setPage,
    setLimit,
    setSearch,
    refresh,
    approveAppointment,
    rejectAppointment,
  } = usePendingAppointments();

  // Local state for search input and rejection dialog
  const [searchText, setSearchText] = useState(search);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Handlers
  const handleSearch = () => {
    setSearch(searchText);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSearch('');
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage + 1);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setLimit(newRowsPerPage);
  };

  const handleApprove = async (id: number) => {
    await approveAppointment(id);
  };

  const handleRejectClick = (id: number) => {
    setRejectingId(id);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (rejectingId && rejectReason.trim()) {
      await rejectAppointment(rejectingId, rejectReason.trim());
      setRejectDialogOpen(false);
      setRejectingId(null);
      setRejectReason('');
    }
  };

  const handleRejectDialogClose = () => {
    setRejectDialogOpen(false);
    setRejectingId(null);
    setRejectReason('');
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/home' },
        { label: 'Agendamentos', path: '/appointments' },
        { label: 'Pendentes de Aprovação' },
      ]}
      title="Agendamentos Pendentes de Aprovação"
      subtitle="Aprove ou rejeite agendamentos fora de horário"
      actions={
        <>
          <HelpButton entity="appointments" screen="pendentes" size="small" />
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={refresh}
          >
            Atualizar
          </Button>
        </>
      }
    >
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Buscar por funcionário..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />

            <Button variant="contained" onClick={handleSearch}>
              Filtrar
            </Button>
            <Button variant="outlined" onClick={handleClearFilters}>
              Limpar
            </Button>
          </Stack>
        </Paper>

        {/* Table */}
        <DataTable
          columns={columns}
          rows={appointments}
          getRowId={(appointment) => appointment.id}
          loading={loading}
          emptyState={{
            title: 'Nenhum agendamento pendente de aprovação',
            description:
              'Aqui ficam os agendamentos criados que aguardam aprovação; assim que são aprovados ou rejeitados, saem desta fila.',
          }}
          actions={[
            {
              icon: <CheckCircle fontSize="small" />,
              tooltip: 'Aprovar',
              color: 'success',
              onClick: (appointment) => handleApprove(appointment.id),
            },
            {
              icon: <Cancel fontSize="small" />,
              tooltip: 'Rejeitar',
              color: 'error',
              onClick: (appointment) => handleRejectClick(appointment.id),
            },
          ]}
          pagination={{
            page: pagination.page - 1,
            rowsPerPage: pagination.limit,
            totalCount: pagination.total,
            onPageChange: handlePageChange,
            onRowsPerPageChange: handleRowsPerPageChange,
            rowsPerPageOptions: [10, 20, 50],
          }}
        />

        {/* Rejection Dialog */}
        <Dialog open={rejectDialogOpen} onClose={handleRejectDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>Rejeitar Agendamento</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Informe o motivo da rejeição. Este campo é obrigatório.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={3}
              label="Motivo da rejeição"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRejectDialogClose}>Cancelar</Button>
            <Button
              onClick={handleRejectConfirm}
              color="error"
              variant="contained"
              disabled={!rejectReason.trim()}
            >
              Rejeitar
            </Button>
          </DialogActions>
        </Dialog>
    </PageShell>
  );
};

export default PendingAppointmentsPage;
